<template>
  <div>
    <v-data-table
      v-model="selectedRows"
      :headers="headers"
      :items="parcelList"
      :items-per-page="10"
      item-key="id"
      show-select
      class="elevation-1"
      :options="stateOptions"
      @update:options="onUpdate"
      @click:row="onParcelClick"
      :search="search"
    >
      <template v-slot:top>
        <v-row
          class="mb-0"
          style="border-bottom: 1px solid rgba(255, 255, 255, 0.12)"
        >
          <v-col class="d-flex align-center">
            <v-dialog persistent v-model="dialog" max-width="900px">
              <template v-slot:activator="{ on }">
                <v-btn small class="ml-4" v-on="on">New Parcel</v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12" sm="4" md="4">
                        <v-text-field
                          ref="nameField"
                          v-model="editedItem.name"
                          label="Name"
                          :rules="[v => !!v || 'Name is required']"
                          required
                          @keyup.enter="save"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="4">
                        <v-select
                          :items="getStatuses"
                          v-model="editedItem.statusId"
                          label="Status"
                          item-text="name"
                          item-value="id"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" sm="4" md="4">
                        <v-text-field
                          v-model="editedItem.externalId"
                          label="External Id"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-subheader class="pl-0">Zoning parameters</v-subheader>
                    <v-row>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.area"
                          label="Area (m²)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.frontageWidth"
                          label="Frontage width (m)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.maxFloors"
                          label="Max floors"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.maxOccupancy"
                          label="Max occupancy (%)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.maxBuildIndex"
                          label="Max build index"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.buildingLineMin"
                          label="Building line min (m)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-text-field
                          dense
                          type="number"
                          v-model.number="editedItem.greeneryMin"
                          label="Greenery min (%)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-select
                          dense
                          multiple
                          chips
                          small-chips
                          :items="roofTypes"
                          v-model="roofTypeArray"
                          label="Roof type"
                          clearable
                        ></v-select>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          dense
                          outlined
                          auto-grow
                          rows="2"
                          v-model="editedItem.properties.description"
                          label="Description"
                          hint="Additional info shown on the plot card (e.g. how a figure was derived)"
                          persistent-hint
                        ></v-textarea>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" sm="4" md="3">
                        <v-switch
                          inset
                          hide-details="auto"
                          v-model="editedItem.energyCorridor"
                          label="Energy corridor"
                        ></v-switch>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-switch
                          inset
                          hide-details="auto"
                          v-model="editedItem.greenBelt"
                          label="Green belt"
                        ></v-switch>
                      </v-col>
                      <v-col cols="12" sm="4" md="3">
                        <v-switch
                          inset
                          hide-details="auto"
                          v-model="editedItem.publish"
                          label="Published"
                        ></v-switch>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="copyDialog" max-width="400px">
              <template v-slot:activator="{ on }">
                <v-btn
                  small
                  class="ml-2"
                  :disabled="selectedRows.length === 0"
                  v-on="on"
                >
                  Copy ({{ selectedRows.length }})
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">Copy parcels</span>
                </v-card-title>
                <v-card-text>
                  <p>
                    Copying {{ selectedRows.length }} parcel{{
                      selectedRows.length === 1 ? '' : 's'
                    }}. Each copy is named "&lt;name&gt; copy (n)" and does not
                    carry over the external id.
                  </p>
                  <v-text-field
                    ref="copyCountField"
                    v-model.number="copyCount"
                    type="number"
                    min="1"
                    label="Number of copies"
                    @keyup.enter="confirmCopy"
                  ></v-text-field>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="copyDialog = false"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="confirmCopy"
                    >Copy</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <v-col cols="2" sm="2" md="2">
            <v-text-field
              dense
              v-model="search"
              append-icon="mdi-magnify"
              outlined=""
              label="Search"
              hide-details
              class="mr-4"
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.name="{ item }">
        <tr>
          {{
            item.name
          }}
        </tr>
      </template>
      <template v-slot:item.action="{ item }">
        <v-icon size="24" @click="deleteItem(item, $event)">close</v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Enums from '../../helpers/Enums'
export default {
  name: 'ParcelList',
  props: {
    parcelList: {
      type: Array,
      required: true,
      default: function() {
        return []
      }
    }
  },
  data: function() {
    return {
      headers: [
        { text: 'PARCEL', align: 'left', sortable: true, value: 'name' },
        { text: 'Actions', value: 'action', sortable: false, align: 'right' }
      ],
      editedIndex: -1,
      editedItem: this.freshParcel(),
      dialog: false,
      mounted: false,
      stateOptions: {},
      search: '',
      roofTypes: ['flat', 'sloped'],
      selectedRows: [],
      copyDialog: false,
      copyCount: 1
    }
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Parcel' : 'Edit Parcel'
    },
    getStatuses() {
      let statuses = []
      for (const property in Enums.ApartmentStatus) {
        statuses.push({
          id: Enums.ApartmentStatus[property],
          name: this.$t(`apartmentStatuses.${property}`)
        })
      }
      return statuses
    },
    // roofType is stored as a comma-separated string (a plot can permit more
    // than one roof type) but v-select's multiple mode needs an array.
    roofTypeArray: {
      get() {
        return this.editedItem.roofType
          ? this.editedItem.roofType.split(',').filter(Boolean)
          : []
      },
      set(val) {
        this.editedItem.roofType = val.length ? val.join(',') : null
      }
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        // wait out the dialog open transition, then focus the name field
        setTimeout(() => {
          if (this.$refs.nameField) this.$refs.nameField.focus()
        }, 200)
      } else {
        this.close()
      }
    },
    copyDialog(val) {
      if (val) {
        this.copyCount = 1
        setTimeout(() => {
          if (this.$refs.copyCountField) this.$refs.copyCountField.focus()
        }, 200)
      }
    }
  },
  created: function() {
    this.stateOptions = localStorage.parcelListOptions
      ? JSON.parse(localStorage.parcelListOptions)
      : {}
  },
  mounted: function() {
    this.mounted = true
  },
  methods: {
    freshParcel() {
      return {
        id: 0,
        name: '',
        masterplansId: 0,
        statusId: 1,
        publish: true,
        externalId: '',
        area: 0,
        frontageWidth: null,
        maxFloors: null,
        energyCorridor: false,
        maxOccupancy: null,
        maxBuildIndex: null,
        buildingLineMin: null,
        roofType: null,
        greeneryMin: null,
        greenBelt: false,
        properties: { description: '' }
      }
    },
    onUpdate(value) {
      if (this.mounted) {
        localStorage.parcelListOptions = JSON.stringify(value)
      }
    },
    onParcelClick(parcel) {
      this.$router
        .push(
          '/masterplans/' +
            this.$route.params.masterplanId +
            '/parcels/' +
            parcel.id
        )
        .catch(err => {})
    },
    deleteItem(item, e) {
      e.stopPropagation()
      this.$store.dispatch('DialogStore/open', {
        title: 'Delete parcel',
        header: false,
        subtitle: 'Are you sure?',
        okText: 'Ok',
        ok: () => {
          this.deleteItemAction(item)
        },
        cancelText: 'Cancel'
      })
    },
    deleteItemAction(item) {
      const index = this.parcelList.indexOf(item)
      this.$store.dispatch('ParcelStore/deleteParcel', item.id).then(res => {
        this.parcelList.splice(index, 1)
      })
    },
    confirmCopy() {
      const copies = Math.max(1, Math.floor(Number(this.copyCount)) || 1)
      this.$store
        .dispatch('ParcelStore/copyParcels', {
          parcels: this.selectedRows,
          copies
        })
        .then(() => {
          this.selectedRows = []
          this.copyDialog = false
        })
    },
    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = this.freshParcel()
        this.editedIndex = -1
      }, 300)
    },
    async save() {
      if (!this.editedItem.name) {
        this.$store.dispatch(
          'NotificationStore/add',
          { type: 'error', message: 'Name is required.' },
          { root: true }
        )
        return
      }
      const masterplan = this.$store.state.MasterplanStore.selectedMasterplan
      const parcel = Object.assign({}, this.editedItem)
      parcel.masterplansId = masterplan.id
      // area is NOT NULL on the server; empty input falls back to 0
      parcel.area =
        parcel.area === '' || parcel.area === null ? 0 : Number(parcel.area)
      this.$store.dispatch('ParcelStore/addParcel', parcel).then(res => {
        this.$store.dispatch('ParcelStore/fetchParcels')
      })
      this.close()
    }
  }
}
</script>

<style scoped></style>
